import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Icon, SemanticICONS } from 'semantic-ui-react';

interface ToggleControlProps {
  buttonLabels: { type: string; icon: SemanticICONS; disabled?: boolean }[];
  setChartType?: (value: string) => void;
  activeToggle?: string;
  defaultToggle?: string;
  size?: 'normal' | 'mini'; // new prop, default is "normal"
}

const ToggleControl: React.FC<ToggleControlProps> = ({
  buttonLabels,
  setChartType = () => {},
  activeToggle,
  defaultToggle,
  size = 'normal', // default to normal
}) => {
  const [internalActive, setInternalActive] = useState<string>(
    () => defaultToggle || buttonLabels[0]?.type || ''
  );

  const isControlled = activeToggle !== undefined;
  const current = isControlled ? activeToggle : internalActive;

  const handleToggle = (chartType: string) => {
    if (!isControlled) setInternalActive(chartType);
    setChartType(chartType);
  };

  useEffect(() => {
    // Set default when component mounts if uncontrolled
    if (!isControlled && defaultToggle) {
      setInternalActive(defaultToggle);
    }
  }, [defaultToggle, isControlled]);

  return (
    <ToggleContainer size={size}>
      {buttonLabels.map(button => (
        <ToggleButton
          key={button.type}
          isActive={current === button.type}
          isDisabled={!!button.disabled}
          size={size}
          onClick={() => !button.disabled && handleToggle(button.type)}
        >
          <Icon name={button.icon} style={{ fontSize: size === 'mini' ? 14 : 20 }} />
        </ToggleButton>
      ))}
    </ToggleContainer>
  );
};

export default ToggleControl;

// Styled Components
const ToggleContainer = styled.div<{ size: 'normal' | 'mini' }>`
  display: flex;
  justify-content: center;
  gap: ${({ size }) => (size === 'mini' ? '4px' : '6px')};
`;

const ToggleButton = styled.button<{
  isActive: boolean;        // selected
  isDisabled: boolean;      // disabled
  size: 'normal' | 'mini';
}>`
  /* palette */
  --bg-selected: #ffffff;
  --fg-selected: #111827;     /* gray-900 */
  --bd-selected: #94a3b8;     /* slate-400 */

  --bg: #f8fafc;              /* slate-50 */
  --bg-hover: #eef2ff;        /* indigo-50 */
  --fg: #374151;              /* gray-700 */
  --fg-hover: #111827;        /* gray-900 */
  --bd: #cbd5e1;              /* slate-300 */
  --bd-hover: #94a3b8;        /* slate-400 */

  --bg-disabled: #f9fafb;     /* gray-50 */
  --fg-disabled: #9ca3af;     /* gray-400 */
  --bd-disabled: #e5e7eb;     /* gray-200 */

  appearance: none;
  border: 1px solid
    ${({ isActive, isDisabled }) =>
      isDisabled ? 'var(--bd-disabled)' : isActive ? 'var(--bd-selected)' : 'var(--bd)'};
  background:
    ${({ isActive, isDisabled }) =>
      isDisabled ? 'var(--bg-disabled)' : isActive ? 'var(--bg-selected)' : 'var(--bg)'};
  color:
    ${({ isActive, isDisabled }) =>
      isDisabled ? 'var(--fg-disabled)' : isActive ? 'var(--fg-selected)' : 'var(--fg)'};

  width: ${({ size }) => (size === 'mini' ? '34px' : '38px')};
  height: ${({ size }) => (size === 'mini' ? '34px' : '38px')};
  min-width: ${({ size }) => (size === 'mini' ? '34px' : '38px')};
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${({ size }) => (size === 'mini' ? '14px' : '14px')};
  font-weight: ${({ isActive }) => (isActive ? 600 : 500)};
  border-radius: 6px;
  line-height: 1;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ isDisabled }) => (isDisabled ? 0.7 : 1)};
  transition: background-color .15s ease, color .15s ease, border-color .15s ease, box-shadow .15s ease, transform .02s ease;

  /* hover (only when enabled) */
  &:hover {
    ${({ isDisabled, isActive }) =>
      !isDisabled &&
      (isActive
        ? `
          border-color: var(--bd-hover);
          color: var(--fg-selected);
        `
        : `
          background: var(--bg-hover);
          border-color: var(--bd-hover);
          color: var(--fg-hover);
        `)}
  }

  /* pressed (only when enabled) */
  &:active {
    ${({ isDisabled }) => !isDisabled && 'transform: translateY(0.5px);'}
  }

  /* keyboard focus ring */
  &:focus-visible {
    outline: 2px solid #3b82f6;   /* blue-500 */
    outline-offset: 2px;
  }

  /* allow using native disabled attr too if you pass it along */
  &:disabled {
    cursor: not-allowed;
    pointer-events: none;
  }
`;
